import { Check, Loader2, Plus } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useStripe } from "../contexts/StripeContext";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { useToast } from "./ui/use-toast";

interface CreditPackOffersProps {
  className?: string;
  successUrl?: string;
  id?: string;
}

function formatCurrency(price: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: Number.isInteger(price) ? 0 : 2,
  }).format(price);
}

export function CreditPackOffers({
  className,
  successUrl,
  id,
}: CreditPackOffersProps) {
  const { availableCreditPacks, createCreditCheckoutSession } = useStripe();
  const { toast } = useToast();
  const [buyingPriceId, setBuyingPriceId] = useState<string | null>(null);
  const checkoutStartInFlightRef = useRef(false);

  const packs = useMemo(
    () =>
      [...availableCreditPacks].sort(
        (a, b) => a.video_credits - b.video_credits,
      ),
    [availableCreditPacks],
  );

  if (packs.length === 0) return null;

  const handleBuy = async (priceId: string) => {
    if (checkoutStartInFlightRef.current) return;
    checkoutStartInFlightRef.current = true;
    setBuyingPriceId(priceId);
    try {
      await createCreditCheckoutSession(priceId, successUrl);
    } catch (error) {
      console.error("Credit checkout failed:", error);
      toast({
        variant: "destructive",
        title: "Couldn't start checkout",
        description:
          "Please try again, or contact support if it keeps failing.",
      });
      setBuyingPriceId(null);
      checkoutStartInFlightRef.current = false;
    }
  };

  return (
    <div
      className={cn(
        "grid gap-5",
        packs.length === 1
          ? "grid-cols-1"
          : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
        className,
      )}
      id={id}
    >
      {packs.map((pack) => {
        const isBuying = buyingPriceId === pack.id;
        const priceLabel = formatCurrency(pack.price, pack.currency);

        return (
          <article
            key={pack.id}
            className={cn(
              "relative rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:border-slate-300 hover:shadow-md",
              packs.length === 1 &&
                "sm:grid sm:grid-cols-[minmax(240px,0.85fr)_minmax(260px,1.15fr)] sm:items-center sm:gap-8",
            )}
          >
            <div>
              <div className="space-y-1">
                <h3 className="font-semibold text-lg text-slate-900">
                  {pack.name}
                </h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-slate-900">
                    {priceLabel}
                  </span>
                  <span className="text-slate-400 text-sm">one-time</span>
                </div>
                <p className="text-xs text-slate-400">Added after checkout</p>
              </div>

              <Button
                type="button"
                className="mt-5 w-full gap-2 bg-slate-900 text-white hover:bg-slate-800"
                onClick={() => handleBuy(pack.id)}
                disabled={!!buyingPriceId}
                aria-label={`Buy ${pack.video_credits} credits for ${priceLabel}`}
              >
                {isBuying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                Buy credits
              </Button>
            </div>

            <div
              className={cn(
                "mt-5 pt-5 border-t border-slate-100",
                packs.length === 1 &&
                  "sm:mt-0 sm:border-l sm:border-t-0 sm:pl-8 sm:pt-0",
              )}
            >
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-600">
                    {pack.video_credits} extra generated-post credits for active
                    subscribers
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-600">
                    Keep your current plan
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-600">
                    Available until used and doesn't count toward your plan's 3x
                    rollover cap
                  </span>
                </li>
              </ul>
            </div>
          </article>
        );
      })}
    </div>
  );
}
